import { api } from "@/lib/api";

const SETUP_PATH = "/upload";
const GUARD_ID = "saved-interview-setup-guard";
const STYLE_ID = "saved-interview-lifecycle-style";
const SAVE_BUTTON_ID = "saved-interview-save-exit-btn";
const SAVE_MODAL_ID = "saved-interview-save-modal";

let installed = false;
let checking = false;
let observer = null;
let eligibilitySessionId = null;
let eligibilityPending = false;
let eligibilityAllowed = false;
let eligibilityLanguage = "en";

const COPY = {
  en: {
    checking: "Checking for a saved interview",
    savedTitle: "You have a saved interview",
    savedBody: "You have an unfinished interview saved from earlier. You may resume it, or start a new interview. Starting a new interview will permanently delete the saved interview.",
    savedNote: "A saved interview is kept for up to 24 hours. Once resumed, it cannot be saved again.",
    resume: "RESUME INTERVIEW",
    newInterview: "NEW INTERVIEW",
    working: "Please wait…",
    resumeError: "Could not resume the saved interview. Please try again.",
    discardError: "Could not delete the saved interview. Please try again.",
    save: "Save & Exit",
    saveTitle: "Save this interview and exit?",
    saveBody: "Your unfinished interview will be saved for 24 hours. You can resume and complete it the next time you log in. Note: If you do not resume the interview within 24 hours, it will be automatically deleted.",
    cancel: "Cancel",
    saveConfirm: "SAVE & EXIT",
    saveError: "Could not save the interview. Please try again.",
    signOutTitle: "Sign out during your interview?",
    signOutBody: "You can save this interview for 24 hours and finish it the next time you sign in, or exit without saving. If you exit without saving, your answers so far are deleted.",
    noSaveUsed: "This interview has already been saved once, so it can't be saved again. If you sign out now, your answers so far are deleted.",
    noSaveTrial: "The Free trial can't be saved. If you sign out now, your interview is deleted.",
    noSaveOther: "This interview can't be saved right now. If you sign out now, your answers so far are deleted.",
    exitNoSave: "EXIT WITHOUT SAVING",
  },
  "pt-BR": {
    checking: "Verificando se há uma entrevista salva",
    savedTitle: "Você tem uma entrevista salva",
    savedBody: "Você tem uma entrevista inacabada salva anteriormente. Retome-a ou inicie uma nova entrevista. Iniciar uma nova entrevista excluirá permanentemente a entrevista salva.",
    savedNote: "Uma entrevista salva é mantida por até 24 horas. Depois de retomada, ela não poderá ser salva novamente.",
    resume: "RETOMAR ENTREVISTA",
    newInterview: "NOVA ENTREVISTA",
    working: "Aguarde…",
    resumeError: "Não foi possível retomar a entrevista salva. Tente novamente.",
    discardError: "Não foi possível excluir a entrevista salva. Tente novamente.",
    save: "Salvar e sair",
    saveTitle: "Salvar esta entrevista e sair?",
    saveBody: "Sua entrevista inacabada será salva por 24 horas. Você poderá retomá-la e concluí-la na próxima vez que entrar. Observação: se você não retomar a entrevista dentro de 24 horas, ela será excluída automaticamente.",
    cancel: "Cancelar",
    saveConfirm: "SALVAR E SAIR",
    saveError: "Não foi possível salvar a entrevista. Tente novamente.",
    signOutTitle: "Sair durante a entrevista?",
    signOutBody: "Você pode salvar esta entrevista por 24 horas e terminá-la na próxima vez que entrar, ou sair sem salvar. Se sair sem salvar, suas respostas até agora serão excluídas.",
    noSaveUsed: "Esta entrevista já foi salva uma vez e não pode ser salva novamente. Se sair agora, suas respostas até agora serão excluídas.",
    noSaveTrial: "O Teste Gratuito não pode ser salvo. Se sair agora, sua entrevista será excluída.",
    noSaveOther: "Esta entrevista não pode ser salva agora. Se sair agora, suas respostas até agora serão excluídas.",
    exitNoSave: "SAIR SEM SALVAR",
  },
  "es-419": {
    checking: "Comprobando si hay una entrevista guardada",
    savedTitle: "Tiene una entrevista guardada",
    savedBody: "Tiene una entrevista sin terminar guardada anteriormente. Reanúdela o inicie una nueva entrevista. Iniciar una nueva entrevista eliminará permanentemente la entrevista guardada.",
    savedNote: "Una entrevista guardada se conserva hasta 24 horas. Una vez reanudada, no podrá guardarse de nuevo.",
    resume: "REANUDAR ENTREVISTA",
    newInterview: "NUEVA ENTREVISTA",
    working: "Espere…",
    resumeError: "No se pudo reanudar la entrevista guardada. Inténtelo de nuevo.",
    discardError: "No se pudo eliminar la entrevista guardada. Inténtelo de nuevo.",
    save: "Guardar y salir",
    saveTitle: "¿Guardar esta entrevista y salir?",
    saveBody: "Su entrevista sin terminar se guardará durante 24 horas. Puede reanudarla y completarla la próxima vez que inicie sesión. Nota: si no reanuda la entrevista dentro de 24 horas, se eliminará automáticamente.",
    cancel: "Cancelar",
    saveConfirm: "GUARDAR Y SALIR",
    saveError: "No se pudo guardar la entrevista. Inténtelo de nuevo.",
    signOutTitle: "¿Cerrar sesión durante la entrevista?",
    signOutBody: "Puede guardar esta entrevista durante 24 horas y terminarla la próxima vez que inicie sesión, o salir sin guardar. Si sale sin guardar, se eliminarán sus respuestas hasta ahora.",
    noSaveUsed: "Esta entrevista ya se guardó una vez y no puede guardarse de nuevo. Si cierra sesión ahora, se eliminarán sus respuestas hasta ahora.",
    noSaveTrial: "La Prueba Gratuita no se puede guardar. Si cierra sesión ahora, se eliminará su entrevista.",
    noSaveOther: "Esta entrevista no se puede guardar en este momento. Si cierra sesión ahora, se eliminarán sus respuestas hasta ahora.",
    exitNoSave: "SALIR SIN GUARDAR",
  },
  "fr-CA": {
    checking: "Vérification d’une entrevue sauvegardée",
    savedTitle: "Vous avez une entrevue sauvegardée",
    savedBody: "Vous avez une entrevue inachevée sauvegardée plus tôt. Reprenez-la ou commencez une nouvelle entrevue. Commencer une nouvelle entrevue supprimera définitivement l’entrevue sauvegardée.",
    savedNote: "Une entrevue sauvegardée est conservée jusqu’à 24 heures. Une fois reprise, elle ne peut plus être sauvegardée de nouveau.",
    resume: "REPRENDRE L’ENTREVUE",
    newInterview: "NOUVELLE ENTREVUE",
    working: "Veuillez patienter…",
    resumeError: "Impossible de reprendre l’entrevue sauvegardée. Veuillez réessayer.",
    discardError: "Impossible de supprimer l’entrevue sauvegardée. Veuillez réessayer.",
    save: "Sauvegarder et quitter",
    saveTitle: "Sauvegarder cette entrevue et quitter?",
    saveBody: "Votre entrevue inachevée sera sauvegardée pendant 24 heures. Vous pourrez la reprendre et la terminer lors de votre prochaine connexion. Remarque : si vous ne reprenez pas l’entrevue dans les 24 heures, elle sera supprimée automatiquement.",
    cancel: "Annuler",
    saveConfirm: "SAUVEGARDER ET QUITTER",
    saveError: "Impossible de sauvegarder l’entrevue. Veuillez réessayer.",
    signOutTitle: "Vous déconnecter pendant l’entrevue?",
    signOutBody: "Vous pouvez sauvegarder cette entrevue pendant 24 heures et la terminer à votre prochaine connexion, ou quitter sans sauvegarder. Si vous quittez sans sauvegarder, vos réponses jusqu’ici seront supprimées.",
    noSaveUsed: "Cette entrevue a déjà été sauvegardée une fois et ne peut pas l’être de nouveau. Si vous vous déconnectez maintenant, vos réponses jusqu’ici seront supprimées.",
    noSaveTrial: "L’essai gratuit ne peut pas être sauvegardé. Si vous vous déconnectez maintenant, votre entrevue sera supprimée.",
    noSaveOther: "Cette entrevue ne peut pas être sauvegardée pour le moment. Si vous vous déconnectez maintenant, vos réponses jusqu’ici seront supprimées.",
    exitNoSave: "QUITTER SANS SAUVEGARDER",
  },
};

function normalizedPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function interviewSessionId() {
  const match = normalizedPath().match(/^\/interview\/([^/]+)$/);
  return match?.[1] || null;
}

function normalizeLanguage(value) {
  const raw = String(value || "en").trim().toLowerCase();
  if (raw.startsWith("pt")) return "pt-BR";
  if (raw.startsWith("es")) return "es-419";
  if (raw.startsWith("fr")) return "fr-CA";
  return "en";
}

function copyFor(value) {
  return COPY[normalizeLanguage(value)] || COPY.en;
}

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes savedInterviewSpin{to{transform:rotate(360deg)}}
    #${SAVE_BUTTON_ID}:focus-visible,
    #${GUARD_ID} button:focus-visible,
    #${SAVE_MODAL_ID} button:focus-visible {
      outline:3px solid var(--brand, #073b75);
      outline-offset:3px;
    }
  `;
  document.head.appendChild(style);
}

function ensureGuard() {
  ensureStyle();
  let guard = document.getElementById(GUARD_ID);
  if (!guard) {
    guard = document.createElement("div");
    guard.id = GUARD_ID;
    guard.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:2147483646",
      "background:#fff",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "padding:24px",
    ].join(";");
    document.body.appendChild(guard);
  }
  return guard;
}

function hideGuard() {
  document.getElementById(GUARD_ID)?.remove();
}

function showCheckingGuard() {
  const guard = ensureGuard();
  guard.setAttribute("role", "status");
  guard.setAttribute("aria-live", "polite");
  guard.setAttribute("aria-label", COPY.en.checking);
  guard.innerHTML = '<span aria-hidden="true" style="width:28px;height:28px;border:3px solid #dbe4ef;border-top-color:#073b75;border-radius:50%;animation:savedInterviewSpin .8s linear infinite"></span>';
}

function showSavedGuard(data) {
  const c = copyFor(data?.interface_language);
  const guard = ensureGuard();
  guard.setAttribute("role", "main");
  guard.removeAttribute("aria-live");
  guard.removeAttribute("aria-label");
  guard.setAttribute("data-testid", "saved-interview-choice");
  guard.innerHTML = `
    <div style="width:100%;max-width:620px;text-align:center;color:#1f2933;font-family:Inter,Arial,sans-serif">
      <img src="/brand/globe-logo.svg" alt="" aria-hidden="true" style="width:86px;height:63px;object-fit:contain;margin:0 auto 20px" />
      <div style="font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#073b75;margin-bottom:18px">English Job Interview Simulator</div>
      <h1 style="font-size:30px;line-height:1.15;margin:0;font-weight:600;letter-spacing:-.02em">${c.savedTitle}</h1>
      <p style="font-size:16px;line-height:1.65;margin:20px auto 0;max-width:560px;color:#5f6b76">${c.savedBody}</p>
      <p style="font-size:13px;line-height:1.55;margin:14px auto 0;max-width:540px;color:#6b7280">${c.savedNote}</p>
      <div style="display:flex;flex-wrap:wrap;justify-content:center;gap:12px;margin-top:30px">
        <button id="${GUARD_ID}-resume" type="button" style="border:0;border-radius:999px;background:#073b75;color:white;padding:12px 22px;font-size:14px;font-weight:600;cursor:pointer">${c.resume}</button>
        <button id="${GUARD_ID}-new" type="button" style="border:1px solid #9a4534;border-radius:999px;background:white;color:#9a4534;padding:12px 22px;font-size:14px;font-weight:600;cursor:pointer">${c.newInterview}</button>
      </div>
      <div id="${GUARD_ID}-status" role="status" aria-live="polite" style="min-height:22px;margin-top:16px;font-size:13px;color:#073b75"></div>
    </div>`;

  const resumeButton = document.getElementById(`${GUARD_ID}-resume`);
  const newButton = document.getElementById(`${GUARD_ID}-new`);
  const status = document.getElementById(`${GUARD_ID}-status`);
  let busy = false;

  const setBusy = (value) => {
    busy = value;
    if (resumeButton) resumeButton.disabled = value;
    if (newButton) newButton.disabled = value;
    if (status) status.textContent = value ? c.working : "";
  };

  resumeButton?.addEventListener("click", async () => {
    if (busy || !data?.session_id) return;
    setBusy(true);
    try {
      await api.post(`/saved-interview/${encodeURIComponent(data.session_id)}/resume`);
      try {
        window.sessionStorage.setItem(`mec_warmup_${data.session_id}`, "1");
      } catch { /* session storage is only a warm-up convenience */ }
      window.location.assign(`/interview/${encodeURIComponent(data.session_id)}`);
    } catch (error) {
      setBusy(false);
      if (status) status.textContent = error?.response?.data?.detail?.message || c.resumeError;
    }
  });

  newButton?.addEventListener("click", async () => {
    if (busy || !data?.session_id) return;
    setBusy(true);
    try {
      await api.post(`/saved-interview/${encodeURIComponent(data.session_id)}/discard`);
      window.location.reload();
    } catch (error) {
      setBusy(false);
      if (status) status.textContent = error?.response?.data?.detail?.message || c.discardError;
    }
  });
}

export async function checkSavedInterviewBeforeSetup() {
  if (typeof window === "undefined" || typeof document === "undefined") return true;
  if (normalizedPath() !== SETUP_PATH || checking) return true;

  checking = true;
  showCheckingGuard();
  try {
    const { data } = await api.get("/saved-interview");
    if (data?.saved && data?.session_id) {
      showSavedGuard(data);
      return false;
    }
    hideGuard();
    return true;
  } catch {
    // Authentication/network handling remains with the normal app flow. The
    // backend creation gate still prevents a saved interview from being bypassed.
    hideGuard();
    return true;
  } finally {
    checking = false;
  }
}

function removeSaveButton() {
  document.getElementById(SAVE_BUTTON_ID)?.remove();
}

function closeSaveModal() {
  document.getElementById(SAVE_MODAL_ID)?.remove();
}

function showSaveModal(sessionId, language) {
  if (document.getElementById(SAVE_MODAL_ID)) return;
  const c = copyFor(language);
  const overlay = document.createElement("div");
  overlay.id = SAVE_MODAL_ID;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", `${SAVE_MODAL_ID}-title`);
  overlay.style.cssText = "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:24px";
  overlay.innerHTML = `
    <div style="width:100%;max-width:540px;border-radius:18px;background:#fff;padding:30px;box-shadow:0 20px 60px rgba(0,0,0,.22);font-family:Inter,Arial,sans-serif;color:#1f2933">
      <h2 id="${SAVE_MODAL_ID}-title" style="font-size:22px;line-height:1.25;margin:0;font-weight:600">${c.saveTitle}</h2>
      <p style="font-size:14px;line-height:1.65;margin:16px 0 0;color:#5f6b76">${c.saveBody}</p>
      <div style="display:flex;justify-content:flex-end;gap:12px;margin-top:26px">
        <button id="${SAVE_MODAL_ID}-cancel" type="button" style="border:1px solid #d8d5cd;border-radius:999px;background:white;color:#374151;padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer">${c.cancel}</button>
        <button id="${SAVE_MODAL_ID}-confirm" type="button" style="border:0;border-radius:999px;background:#073b75;color:white;padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer">${c.saveConfirm}</button>
      </div>
      <div id="${SAVE_MODAL_ID}-status" role="status" aria-live="polite" style="min-height:20px;margin-top:12px;font-size:13px;color:#9a4534"></div>
    </div>`;
  document.body.appendChild(overlay);

  const cancelButton = document.getElementById(`${SAVE_MODAL_ID}-cancel`);
  const confirmButton = document.getElementById(`${SAVE_MODAL_ID}-confirm`);
  const status = document.getElementById(`${SAVE_MODAL_ID}-status`);
  let busy = false;

  const cancel = () => {
    if (!busy) closeSaveModal();
  };
  cancelButton?.addEventListener("click", cancel);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) cancel();
  });

  confirmButton?.addEventListener("click", async () => {
    if (busy) return;
    busy = true;
    if (cancelButton) cancelButton.disabled = true;
    if (confirmButton) confirmButton.disabled = true;
    if (status) status.textContent = "";
    try {
      await api.post(`/sessions/${encodeURIComponent(sessionId)}/save-exit`);
      try {
        await api.post("/auth/logout");
      } catch { /* saved interview remains valid even if logout delivery fails */ }
      window.location.assign("/login?interview_saved=1");
    } catch (error) {
      busy = false;
      if (cancelButton) cancelButton.disabled = false;
      if (confirmButton) confirmButton.disabled = false;
      const detail = error?.response?.data?.detail;
      if (status) status.textContent = detail?.message || (typeof detail === "string" ? detail : c.saveError);
    }
  });

  window.setTimeout(() => confirmButton?.focus(), 0);
}


const SIGN_OUT_MODAL_ID = "saved-interview-sign-out-modal";

/**
 * Sign out during an interview (product owner, 2026-11-01): ask first. Resolves to
 * "exit" (sign out without saving) or "cancel"; Save & Exit saves, signs out and
 * leaves for the sign-in page itself, like the Save & Exit button. Saving is offered
 * whenever the server would accept it, including during the warm-up.
 */
export async function confirmSignOutDuringInterview(sessionId, language) {
  if (!sessionId || typeof document === "undefined") return "exit";
  let session = null;
  try {
    ({ data: session } = await api.get(`/sessions/${encodeURIComponent(sessionId)}`));
  } catch { /* offered without saving */ }
  const c = copyFor(language || session?.interface_language);
  const canSave = session?.status === "in_progress" && !session?.save_exit_used && !session?.free_trial;
  const body = canSave ? c.signOutBody
    : session?.free_trial ? c.noSaveTrial
    : session?.save_exit_used ? c.noSaveUsed
    : c.noSaveOther;

  ensureStyle();
  document.getElementById(SIGN_OUT_MODAL_ID)?.remove();
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = SIGN_OUT_MODAL_ID;
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", `${SIGN_OUT_MODAL_ID}-title`);
    overlay.setAttribute("data-testid", "sign-out-interview-prompt");
    overlay.style.cssText = "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px";
    const button = "border-radius:999px;padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer;min-height:44px";
    overlay.innerHTML = `
      <div style="width:100%;max-width:540px;border-radius:18px;background:#fff;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,.22);font-family:Inter,Arial,sans-serif;color:#1f2933">
        <h2 id="${SIGN_OUT_MODAL_ID}-title" style="font-size:22px;line-height:1.25;margin:0;font-weight:600">${c.signOutTitle}</h2>
        <p style="font-size:14px;line-height:1.65;margin:16px 0 0;color:#5f6b76">${body}</p>
        <div style="display:flex;flex-wrap:wrap;justify-content:flex-end;gap:12px;margin-top:26px">
          <button id="${SIGN_OUT_MODAL_ID}-cancel" type="button" data-testid="sign-out-cancel" style="border:1px solid #d8d5cd;background:white;color:#374151;${button}">${c.cancel}</button>
          <button id="${SIGN_OUT_MODAL_ID}-exit" type="button" data-testid="sign-out-exit" style="border:1px solid #9a4534;background:white;color:#9a4534;${button}">${c.exitNoSave}</button>
          ${canSave ? `<button id="${SIGN_OUT_MODAL_ID}-save" type="button" data-testid="sign-out-save" style="border:0;background:#073b75;color:white;${button}">${c.saveConfirm}</button>` : ""}
        </div>
        <div id="${SIGN_OUT_MODAL_ID}-status" role="status" aria-live="polite" style="min-height:20px;margin-top:12px;font-size:13px;color:#9a4534"></div>
      </div>`;
    document.body.appendChild(overlay);

    const cancelButton = document.getElementById(`${SIGN_OUT_MODAL_ID}-cancel`);
    const exitButton = document.getElementById(`${SIGN_OUT_MODAL_ID}-exit`);
    const saveButton = document.getElementById(`${SIGN_OUT_MODAL_ID}-save`);
    const status = document.getElementById(`${SIGN_OUT_MODAL_ID}-status`);
    let busy = false;

    const finish = (choice) => {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(choice);
    };
    const onKey = (event) => {
      if (event.key === "Escape" && !busy) finish("cancel");
    };
    document.addEventListener("keydown", onKey);
    cancelButton?.addEventListener("click", () => { if (!busy) finish("cancel"); });
    overlay.addEventListener("click", (event) => { if (event.target === overlay && !busy) finish("cancel"); });
    exitButton?.addEventListener("click", () => { if (!busy) finish("exit"); });
    saveButton?.addEventListener("click", async () => {
      if (busy) return;
      busy = true;
      for (const b of [cancelButton, exitButton, saveButton]) if (b) b.disabled = true;
      if (status) status.textContent = "";
      try {
        await api.post(`/sessions/${encodeURIComponent(sessionId)}/save-exit`);
        try {
          await api.post("/auth/logout");
        } catch { /* saved interview remains valid even if logout delivery fails */ }
        document.removeEventListener("keydown", onKey);
        window.location.assign("/login?interview_saved=1");
      } catch (error) {
        busy = false;
        for (const b of [cancelButton, exitButton, saveButton]) if (b) b.disabled = false;
        const detail = error?.response?.data?.detail;
        if (status) status.textContent = detail?.message || (typeof detail === "string" ? detail : c.saveError);
      }
    });
    window.setTimeout(() => (saveButton || cancelButton)?.focus(), 0);
  });
}

async function resolveSaveEligibility(sessionId) {
  if (!sessionId || eligibilityPending) return;
  if (eligibilitySessionId === sessionId) {
    if (eligibilityAllowed) attachSaveButton();
    return;
  }

  eligibilityPending = true;
  eligibilitySessionId = sessionId;
  eligibilityAllowed = false;
  try {
    const { data } = await api.get(`/sessions/${encodeURIComponent(sessionId)}`);
    eligibilityLanguage = data?.interface_language || data?.language || "en";
    eligibilityAllowed = data?.status === "in_progress" && !data?.save_exit_used && !data?.free_trial;
  } catch {
    eligibilityAllowed = false;
  } finally {
    eligibilityPending = false;
  }
  attachSaveButton();
}

function attachSaveButton() {
  const sessionId = interviewSessionId();
  const warmupVisible = Boolean(document.querySelector('[data-testid="warmup-gate"]'));
  if (!sessionId || warmupVisible) {
    removeSaveButton();
    return;
  }

  if (eligibilitySessionId !== sessionId) {
    removeSaveButton();
    void resolveSaveEligibility(sessionId);
    return;
  }
  if (!eligibilityAllowed) {
    removeSaveButton();
    return;
  }
  if (document.getElementById(SAVE_BUTTON_ID)) return;

  const endButton = document.querySelector('[data-testid="end-interview-btn"]');
  const parent = endButton?.parentElement;
  if (!endButton || !parent) return;

  ensureStyle();
  const c = copyFor(eligibilityLanguage);
  const button = document.createElement("button");
  button.id = SAVE_BUTTON_ID;
  button.type = "button";
  button.setAttribute("data-testid", "save-exit-interview-btn");
  button.className = "inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/5 px-4 py-2 text-sm";
  button.textContent = c.save;
  button.addEventListener("click", () => showSaveModal(sessionId, eligibilityLanguage));
  parent.insertBefore(button, endButton);
}

function resetInterviewEligibilityIfNeeded() {
  const sessionId = interviewSessionId();
  if (sessionId !== eligibilitySessionId) {
    eligibilitySessionId = null;
    eligibilityPending = false;
    eligibilityAllowed = false;
    eligibilityLanguage = "en";
    removeSaveButton();
  }
  attachSaveButton();
}

export function installSavedInterviewLifecycle() {
  if (installed || typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;
  ensureStyle();

  const schedule = () => {
    if (normalizedPath() === SETUP_PATH) {
      showCheckingGuard();
      window.setTimeout(() => {
        void checkSavedInterviewBeforeSetup();
      }, 0);
    } else {
      hideGuard();
    }
    resetInterviewEligibilityIfNeeded();
  };

  for (const method of ["pushState", "replaceState"]) {
    const original = window.history[method];
    window.history[method] = function patchedSavedInterviewHistory(...args) {
      const result = original.apply(this, args);
      schedule();
      return result;
    };
  }
  window.addEventListener("popstate", schedule);

  observer = new MutationObserver(() => {
    resetInterviewEligibilityIfNeeded();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.setTimeout(resetInterviewEligibilityIfNeeded, 0);
}
